import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FileSignature, CheckCircle, Clock, XCircle } from 'lucide-react';
import EmployeePortalCrudShell from './EmployeePortalCrudShell';
import { statusBadge } from './employeePortalShared';
import { formatPortalDate, formatPortalMoney } from './employeePortalSaudiFormat';
import {
  normalizeContractType,
  getNoticePeriodDays,
  SAUDI_PROBATION_DAYS_DEFAULT,
  buildSaudiSalaryPackage,
} from '../ressources-humaines/utils/hrSaudiFormat';
import { calculateEOSBProvision } from '../salaires/utils/payrollSaudiFormat';
import { usePortalEmployeeContext } from './PortalEmployeeContext';

const NS = 'contractsPage';
const API = '/api/hr/contracts';

const CONTRACT_STATUS = [
  { value: 'actif', labelKey: 'contract_active' },
  { value: 'expire', labelKey: 'contract_expired' },
  { value: 'resilie', labelKey: 'contract_terminated' },
];

const EMPTY = {
  contractType: '', position: '', department: '', startDate: '', endDate: '',
  basicSalary: '', housingAllowance: '', transportAllowance: '', totalPackage: '',
  probationPeriod: '', noticePeriodDays: '', eosbAccrual: '', status: '',
};

export default function EmployeePortalContractsPage() {
  const { t, i18n } = useTranslation();
  const { employeeId } = usePortalEmployeeContext();
  const apiQueryParams = useMemo(() => (employeeId ? { employee_id: employeeId } : {}), [employeeId]);

  const contractTypeLabel = (type) => {
    const normalized = normalizeContractType(type);
    const key = `contract_type_${normalized}`;
    return t(`modules.employee_portal.pages.common.${key}`, { defaultValue: normalized });
  };

  const mapContract = (r) => {
    const basic = parseFloat(r.basicSalary ?? r.salary) || 0;
    const pkg = buildSaudiSalaryPackage(basic, {
      housing: r.housingAllowance,
      transport: r.transportAllowance,
      mobile: r.mobileAllowance,
      food: r.foodAllowance,
    });
    const paymentFrequency = r.paymentFrequency || 'monthly';
    return {
      contractType: normalizeContractType(r.contractType || r.typeContrat),
      position: r.position || '',
      department: r.department || '',
      startDate: r.startDate || '',
      endDate: r.endDate || '',
      basicSalary: basic ? formatPortalMoney(basic) : '',
      housingAllowance: pkg.housingAllowance ? formatPortalMoney(pkg.housingAllowance) : '—',
      transportAllowance: pkg.transportAllowance ? formatPortalMoney(pkg.transportAllowance) : '—',
      totalPackage: (r.totalPackage ?? pkg.totalPackage) ? formatPortalMoney(r.totalPackage ?? pkg.totalPackage) : '—',
      probationPeriod: String(r.probationPeriod ?? r.periodeEssai ?? SAUDI_PROBATION_DAYS_DEFAULT),
      noticePeriodDays: String(r.noticePeriodDays ?? getNoticePeriodDays(paymentFrequency)),
      eosbAccrual: basic ? formatPortalMoney(calculateEOSBProvision(basic)) : '',
      status: r.status || '',
    };
  };

  const renderContractType = (r) => contractTypeLabel(r.contractType || r.typeContrat);
  const renderDate = (key) => (r) => formatPortalDate(r[key], i18n.language);
  const renderMoney = (key) => (r) => {
    const val = r[key];
    return val != null && val !== '' ? formatPortalMoney(val) : '—';
  };

  const computeContractStats = (records) => [
    { titleKey: 'stat_total', value: records.length, icon: FileSignature, color: 'from-teal-500 to-teal-600', variant: 'copper' },
    {
      titleKey: 'stat_active',
      value: records.filter((r) => r.status === 'actif').length,
      icon: CheckCircle,
      color: 'from-green-500 to-green-600',
      variant: 'forest',
    },
    {
      titleKey: 'stat_expired',
      value: records.filter((r) => r.status === 'expire').length,
      icon: Clock,
      color: 'from-amber-500 to-amber-600',
      variant: 'forest',
    },
    {
      titleKey: 'stat_terminated',
      value: records.filter((r) => r.status === 'resilie').length,
      icon: XCircle,
      color: 'from-red-500 to-red-600',
      variant: 'forest',
    },
  ];

  return (
    <EmployeePortalCrudShell
      ns={NS}
      icon={FileSignature}
      apiPath={API}
      apiQueryParams={apiQueryParams}
      requireEmployeeScope
      allowCreate={false}
      allowEdit={false}
      allowDelete={false}
      gradient="from-blue-500 to-blue-700"
      emptyForm={EMPTY}
      mapRecordToForm={mapContract}
      computeStats={computeContractStats}
      columns={[
        { key: 'contractType', labelKey: 'col_type', render: renderContractType },
        { key: 'position', labelKey: 'col_position' },
        { key: 'startDate', labelKey: 'col_start', render: renderDate('startDate') },
        { key: 'totalPackage', labelKey: 'col_total_package', render: renderMoney('totalPackage') },
        { key: 'status', labelKey: 'col_status', render: (r, _p, _t, pl) => statusBadge(r.status, pl, CONTRACT_STATUS) },
      ]}
      formSections={[
        {
          id: 'contract',
          titleKey: 'section_contract',
          fields: [
            { key: 'contractType', labelKey: 'col_type', readOnly: true },
            { key: 'position', labelKey: 'col_position', readOnly: true },
            { key: 'department', labelKey: 'col_department', readOnly: true },
            { key: 'startDate', labelKey: 'col_start', readOnly: true },
            { key: 'endDate', labelKey: 'col_end', readOnly: true },
            { key: 'status', labelKey: 'col_status', readOnly: true },
          ],
        },
        {
          id: 'compensation',
          titleKey: 'section_compensation',
          fields: [
            { key: 'basicSalary', labelKey: 'field_basic_salary', readOnly: true },
            { key: 'housingAllowance', labelKey: 'field_housing_allowance', readOnly: true },
            { key: 'transportAllowance', labelKey: 'field_transport_allowance', readOnly: true },
            { key: 'totalPackage', labelKey: 'col_total_package', readOnly: true },
            { key: 'probationPeriod', labelKey: 'field_probation', readOnly: true },
            { key: 'noticePeriodDays', labelKey: 'field_notice_period', readOnly: true },
            { key: 'eosbAccrual', labelKey: 'field_eosb_accrual', readOnly: true, fullWidth: true },
          ],
        },
      ]}
    />
  );
}
